import { VehicleFeature } from '@/catalog/types/vehicle';
import { ICONS_MAP } from '@/common/utils/category-mappings';
import { colors } from '@/theme/colors';
import { cn } from '@/shadcn-ui/utils';
import Spacer from '@/common/components/spacer';

interface FeatureGroupProps {
  groupedFeatures?: Record<string, VehicleFeature[]>;
  isFullFeatureListView?: boolean;
  className?: string;
}

export default function FeatureGroup({
  groupedFeatures,
  isFullFeatureListView = false,
}: FeatureGroupProps) {
  if (!groupedFeatures || Object.keys(groupedFeatures).length === 0) {
    return null;
  }

  return (
    <>
      {isFullFeatureListView ? <Spacer variant="height8" /> : null}

      <h2
        className={cn(
          'pdp-header text-[22px] leading-[32px] text-flexcar-neutral90 md:text-[32px] md:leading-normal',
          {
            'text-flexcar-neutral1': isFullFeatureListView,
          }
        )}
      >
        Features
      </h2>

      {isFullFeatureListView ? <Spacer variant="height24" /> : null}

      <div
        className={cn(
          'grid w-full grid-cols-1 gap-y-6 border-flexcar-neutral70 md:grid-cols-[repeat(var(--feature-col-md),_minmax(0,_1fr))] md:gap-x-[40px] md:gap-y-[40px] lg:grid-cols-[repeat(var(--feature-col-lg),_minmax(0,_1fr))] xl:w-[984px] xl:gap-x-[72px]',
          {
            'gap-y-[40px]': isFullFeatureListView,
          }
        )}
        style={
          {
            '--feature-col-lg': Object.keys(groupedFeatures).length,
            '--feature-col-md': Math.min(Object.keys(groupedFeatures).length, 2),
          } as React.CSSProperties
        }
      >
        {Object.entries(groupedFeatures).map(([category, features]) => {
          //  Map `category` to the correct display name
          const categoryDisplayName = category;

          // Ensure TypeScript allows indexing ICONS_MAP
          const IconComponent = ICONS_MAP[categoryDisplayName as keyof typeof ICONS_MAP];

          return (
            <div key={category}>
              <h4
                className={cn(
                  'pdp-header mb-4 flex items-center gap-2 border-b pb-2 text-[18px] leading-[24px] text-flexcar-neutral90 sm:text-[18px] sm:leading-[24px] md:flex-col md:items-start md:gap-4 md:text-[22px] md:leading-[32px]',
                  {
                    'flex flex-col items-start gap-4 text-flexcar-neutral1':
                      isFullFeatureListView,
                  }
                )}
              >
                {IconComponent ? (
                  <IconComponent
                    className="h-[24px] w-[24px] md:h-[32px] md:w-[32px]"
                    fillPrimary={
                      isFullFeatureListView ? colors.neutral1 : colors.neutral90
                    }
                  />
                ) : null}
                {categoryDisplayName}
              </h4>

              <div className={cn({ 'hidden md:block': !isFullFeatureListView })}>
                {/**
                 * Padding math:
                 * Single line should be 46px line height. To keep that while having a 8px gap between wrapping lines:
                 * 22px line height is the starting point to give 4px gap above and below the text (font size 18px). Wrapping lines will stack
                 * without additional gap so actual gap between text is 8px (4px above + 4px below).
                 *
                 * For a single line to maintain 46px line height:
                 * 22px line height + 24px vertical padding = 46px
                 *
                 * i.e. 22px line height + 12px top padding + 12px bottom padding = 46px
                 */}
                {features.map((feature) => (
                  <p
                    key={feature.key}
                    className={cn(
                      'pdp-header py-[12px] text-[18px] font-light leading-[22px] text-flexcar-neutral90',
                      {
                        'text-flexcar-neutral1': isFullFeatureListView,
                      }
                    )}
                  >
                    {feature.display_name}
                  </p>
                ))}
              </div>

              {/**
               * Padding math:
               * Single line should be 32px line height. To keep that while having a 8px gap between wrapping lines:
               * 20px line height is the starting point to give 4px gap above and below the text (font size 16px). Wrapping lines will stack
               * without additional gap so actual gap between text is 8px (4px above + 4px below).
               *
               * For a single line to maintain 32px line height:
               * 22px line height + 24px vertical padding = 46px
               *
               * i.e. 22px line height + 12px top padding + 12px bottom padding = 46px
               */}
              {!isFullFeatureListView ? (
                <div className="md:hidden">
                  {features.slice(0, 3).map((feature) => (
                    <p
                      key={feature.key}
                      className="pdp-header py-[6px] text-[16px] font-light leading-[20px] text-flexcar-neutral90"
                    >
                      {feature.display_name}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}
